import { useMemo } from 'react';
import { Node, Edge, MarkerType } from '@xyflow/react';
import { getLDG } from '../../../../governance';
import { getModuleColors, MODULES } from '../../../../content';
export type LdgNodeData = {
  label: string;
  module: string;
  skill: string;
  subSkill: string;
  tier: number;
  levelCode: string;
  hasContent: boolean;
  colorSet: ReturnType<typeof getModuleColors>;
  prerequisites: string[];
  dependents: string[];
};
export function useLdgGraphData(activeModules: Set<string>) {
  return useMemo(() => {
    const ldg = getLDG();
    const nodes: Node<LdgNodeData>[] = [];
    const edges: Edge[] = [];

    // 1. Filter nodes based on active modules
    const filteredFamilyIds = new Set<string>();
    for (const [familyId, node] of ldg.nodes.entries()) {
      if (activeModules.size === 0 || activeModules.has(node.module)) {
        filteredFamilyIds.add(familyId);
      }
    }

    // 2. Group nodes by depth for layout
    const nodesByDepth = new Map<number, string[]>();
    let maxDepth = 0;
    for (const familyId of filteredFamilyIds) {
      const node = ldg.nodes.get(familyId);
      if (!node) continue;
      const depth = node.depth;
      maxDepth = Math.max(maxDepth, depth);
      const depthGroup = nodesByDepth.get(depth) || [];
      depthGroup.push(familyId);
      nodesByDepth.set(depth, depthGroup);
    }

    // 3. Create ReactFlow nodes with calculated positions
    const X_SPACING = 350;
    const Y_SPACING = 100;
    for (let depth = 0; depth <= maxDepth; depth++) {
      const depthGroup = nodesByDepth.get(depth) || [];

      // Sort within depth group by tier, then module, then familyId
      depthGroup.sort((a, b) => {
        const nodeA = ldg.nodes.get(a);
        const nodeB = ldg.nodes.get(b);
        if (!nodeA || !nodeB) return 0;
        if (nodeA.tier !== nodeB.tier) return nodeA.tier - nodeB.tier;
        if (nodeA.module !== nodeB.module) return nodeA.module.localeCompare(nodeB.module);
        return a.localeCompare(b);
      });
      const startY = -((depthGroup.length - 1) * Y_SPACING) / 2;
      depthGroup.forEach((familyId, index) => {
        const node = ldg.nodes.get(familyId);
        if (!node) return;
        const moduleDef = MODULES.find(m => m.id === node.module);
        const colorSet = getModuleColors(moduleDef?.color || 'text-indigo-500');
        nodes.push({
          id: familyId,
          type: 'ldgNode',
          position: {
            x: depth * X_SPACING,
            y: startY + index * Y_SPACING
          },
          data: {
            label: familyId,
            module: node.module,
            skill: node.skill,
            subSkill: node.subSkill,
            tier: node.tier,
            levelCode: node.levelCode,
            hasContent: node.hasContent,
            colorSet,
            prerequisites: node.prerequisites,
            dependents: node.dependents
          }
        });
      });
    }

    // 4. Create ReactFlow edges
    for (const edge of ldg.edges) {
      // Only add edge if both source and target are in the filtered set
      if (filteredFamilyIds.has(edge.from) && filteredFamilyIds.has(edge.to)) {
        edges.push({
          id: `${edge.from}-${edge.to}`,
          source: edge.from,
          target: edge.to,
          type: 'smoothstep',
          animated: edge.type === 'soft',
          style: {
            stroke: edge.type === 'hard' ? '#94a3b8' : '#cbd5e1',
            strokeWidth: edge.type === 'hard' ? 2 : 1.5,
            strokeDasharray: edge.type === 'soft' ? '5,5' : 'none'
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edge.type === 'hard' ? '#94a3b8' : '#cbd5e1'
          }
        });
      }
    }
    return {
      nodes,
      edges,
      stats: ldg.metadata
    };
  }, [activeModules]);
}